import { FormProvider } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { Separator } from '~/components';
import { Button } from '~/components/Button';
import { FormFieldRadioGroup } from '~/components/FormField/components/FormFieldRadioGroup';
import { useIsExternalUser } from '~/hooks/useIsExternalUser';
import { FEATURE_FLAGS } from '~/modules/experiments/features';
import { useFeatureFlags } from '~/modules/experiments/hooks/useFeatureFlags';
import { useLabelGroupList } from '~/modules/settings/api/queries/labelGroupList';
import { Drawer } from '~/modules/settings/components/Drawer';
import { SelectLabelGroups } from '~/modules/settings/components/SelectLabelGroups';
import { LABEL_TYPES } from '~/modules/settings/constants/Filter';
import { useFilterForm } from '~/modules/settings/hooks/useFilterForm';
import type { LabelsFilter } from '~/modules/settings/types/filter';
import { translateOptions } from '~/modules/settings/utils/helpers';
import { toDropdownList } from '~/modules/settings/utils/transformers';
import { DEFAULT_FILTER_LIMIT } from '~settings/constants';

const defaultValues: LabelsFilter = {
  labelType: 'all',
  labelGroupIds: [],
};

interface LabelsFormFilterProps {
  open: boolean;
  onApply: () => void;
}

export function LabelFormFilter({ open, onApply }: LabelsFormFilterProps) {
  const { t } = useTranslation('settings');
  const [searchParams] = useSearchParams();

  const [labelGroupsEnabled] = useFeatureFlags([
    FEATURE_FLAGS.ContainerSettingsLabelGroups,
  ]);

  const isCurrentUserInternal = !useIsExternalUser();

  const labelGroupIds = searchParams.get('labelGroupIds');
  const shouldLoadLabelGroups =
    labelGroupsEnabled && isCurrentUserInternal && !!labelGroupIds;

  const { labelGroupList } = useLabelGroupList(
    { labelGroupIds: labelGroupIds ?? '' },
    {
      select: (data) => toDropdownList(data.data),
      enabled: shouldLoadLabelGroups,
    },
  );

  const { filterForm, handleApply, handleClear } = useFilterForm<LabelsFilter>({
    onApply,
    isOpen: open,
    values: {
      ...defaultValues,
      ...(labelGroupList ? { labelGroupIds: labelGroupList } : {}),
    },
  });

  return (
    <FormProvider {...filterForm}>
      <Drawer.Header>
        <Drawer.Title>{t('filters')}</Drawer.Title>
      </Drawer.Header>
      <Drawer.Body>
        <FormFieldRadioGroup
          name="labelType"
          label={t('labelType')}
          options={translateOptions(LABEL_TYPES, t)}
          className="gap-md"
        />
        {isCurrentUserInternal && labelGroupsEnabled ? (
          <>
            <Separator className="my-md" />
            <SelectLabelGroups
              maxSelectedOptions={DEFAULT_FILTER_LIMIT}
              name="labelGroupIds"
            />
          </>
        ) : null}
      </Drawer.Body>
      <Drawer.Footer>
        <Button
          size="large"
          variant="tertiary"
          onClick={handleClear}
          className="font-normal"
          type="button"
        >
          {t('Clear filters')}
        </Button>
        <Button
          size="large"
          type="button"
          onClick={filterForm.handleSubmit(handleApply)}
        >
          {t('Apply')}
        </Button>
      </Drawer.Footer>
    </FormProvider>
  );
}

LabelFormFilter.displayName = 'LabelFormFilter';
